/**
 * OtviraciDobaFields — the pickup / return hours of a pobočka as seven weekday rows: a day is closed, or open between two
 * `HH:mm` times. Controlled by the pobočka form body; the server owns the rules (`hodinyChyba` only gives early feedback).
 */

import { Button } from '@/components/Button';
import { Checkbox } from '@/components/Checkbox';
import { TextInput } from '@/components/TextInput';
import { DNY, HHMM_PATTERN, hodinyChyba, hodinyPracovniDny, type DenRadek } from './otviraci-doba-helpers';

interface Props {
  value: DenRadek[];
  onChange: (next: DenRadek[]) => void;
  disabled?: boolean;
}

export function OtviraciDobaFields({ value, onChange, disabled }: Props) {
  const setDen = (i: number, patch: Partial<DenRadek>) => onChange(value.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const chyba = hodinyChyba(value);

  return (
    <div data-testid="otviraci-doba" className="col-span-full flex flex-col gap-2">
      <div className="flex items-center justify-between gap-3">
        <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-ink-3">Otevírací doba (předání a vrácení)</span>
        <Button variant="secondary" size="sm" type="button" disabled={disabled} onClick={() => onChange(hodinyPracovniDny())}>
          Pracovní dny 8–15
        </Button>
      </div>
      <div className="flex flex-col border border-line">
        {DNY.map((den, i) => {
          const r = value[i];
          return (
            <div key={den} className="grid grid-cols-[minmax(120px,1fr)_auto_auto] items-center gap-3 border-b border-line px-3 py-2 last:border-b-0">
              <Checkbox
                label={den}
                checked={r.otevreno}
                disabled={disabled}
                onChange={(e) => setDen(i, { otevreno: e.currentTarget.checked })}
              />
              <span className="inline-flex w-[88px] border-b border-line focus-within:border-accent">
                <TextInput
                  aria-label={`${den} od`}
                  value={r.od}
                  maxLength={5}
                  inputMode="numeric"
                  placeholder="08:00"
                  pattern={HHMM_PATTERN}
                  title="Čas ve tvaru HH:mm"
                  disabled={disabled || !r.otevreno}
                  onChange={(e) => setDen(i, { od: e.currentTarget.value })}
                />
              </span>
              <span className="inline-flex w-[88px] border-b border-line focus-within:border-accent">
                <TextInput
                  aria-label={`${den} do`}
                  value={r.do}
                  maxLength={5}
                  inputMode="numeric"
                  placeholder="15:00"
                  pattern={HHMM_PATTERN}
                  title="Čas ve tvaru HH:mm"
                  disabled={disabled || !r.otevreno}
                  onChange={(e) => setDen(i, { do: e.currentTarget.value })}
                />
              </span>
            </div>
          );
        })}
      </div>
      {chyba ? (
        <p role="alert" className="text-[12.5px] text-red-text">
          {chyba}
        </p>
      ) : (
        <p className="text-[12.5px] text-ink-3">Zavřený den (bez zaškrtnutí) web nenabídne. Bez jediného otevřeného dne se pobočka na webu nezobrazí.</p>
      )}
    </div>
  );
}
